import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Lock } from "lucide-react";
import Reveal from "../Reveal";
import ApplicationForm from "./ApplicationForm";
import BookingCalendar from "./BookingCalendar";
import { FINAL_BENEFITS } from "../../data/content";

function Steps({ step }) {
  const items = ["Formulario", "Calendario", "Confirmación"];
  return (
    <div className="flex items-center justify-center gap-3 md:gap-5 mb-10">
      {items.map((label, i) => {
        const idx = i + 1;
        const done = step > idx;
        const active = step === idx;
        return (
          <div key={label} className="flex items-center gap-3 md:gap-5">
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border transition-colors ${
                  done
                    ? "bg-[#D4AF37] border-[#D4AF37] text-black"
                    : active
                    ? "border-[#D4AF37] text-[#D4AF37]"
                    : "border-white/20 text-white/30"
                }`}
              >
                {done ? <CheckCircle2 size={14} /> : idx}
              </div>
              <span className={`hidden sm:block text-[11px] uppercase tracking-[0.15em] font-display ${active || done ? "text-white" : "text-white/30"}`}>
                {label}
              </span>
            </div>
            {i < items.length - 1 && <div className={`w-6 md:w-10 h-px ${done ? "bg-[#D4AF37]" : "bg-white/15"}`} />}
          </div>
        );
      })}
    </div>
  );
}

export default function Conversion() {
  const [step, setStep] = useState(1);
  const [leadId, setLeadId] = useState(null);
  const [name, setName] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  const onFormSuccess = (id, nm) => {
    setLeadId(id);
    setName(nm);
    setStep(2);
    setTimeout(() => document.getElementById("reserva")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const onBooked = (data) => {
    setConfirmation(data);
    setStep(3);
    setTimeout(() => document.getElementById("reserva")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  return (
    <section id="reserva" data-testid="conversion-section" className="relative py-24 md:py-36 bg-[#030303] overflow-hidden scroll-mt-20">
      <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12">
        <Reveal className="text-center mb-4">
          <span className="block uppercase tracking-[0.4em] text-[10px] md:text-xs font-semibold text-[#D4AF37] mb-7 font-display">
            Acto final · Tu decisión
          </span>
          <h2 className="font-serif font-medium tracking-tight text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
            Reserva tu <span className="italic gold-text">valoración gratuita</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="text-center mb-10">
          <p className="font-serif italic text-white/90 text-xl md:text-2xl leading-snug max-w-[40ch] mx-auto">
            Si has llegado hasta aquí, probablemente no buscas otra dieta. Buscas dejar de empezar de cero.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="bg-[#070707] border border-white/[0.07] rounded-3xl p-6 md:p-10">
            <Steps step={step} />

            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="form" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}>
                  <div className="flex items-center gap-2 mb-6 text-white/50 text-xs">
                    <Lock size={14} className="text-[#D4AF37]" />
                    Completa tus datos para desbloquear el calendario de reservas.
                  </div>
                  <ApplicationForm onSuccess={onFormSuccess} />
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="cal" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}>
                  <BookingCalendar leadId={leadId} name={name} onBooked={onBooked} />
                </motion.div>
              )}

              {step === 3 && confirmation && (
                <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="text-center py-6" data-testid="booking-confirmation">
                  <div className="w-16 h-16 rounded-full gold-gradient-bg flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 size={32} className="text-black" />
                  </div>
                  <h3 className="font-display font-bold uppercase text-2xl md:text-3xl mb-3">¡Reserva confirmada!</h3>
                  <p className="text-white/60 mb-6">
                    {confirmation.name}, tu valoración gratuita está reservada para:
                  </p>
                  <div className="inline-block border border-[#D4AF37]/40 rounded-sm px-8 py-5 mb-8">
                    <p className="font-display uppercase tracking-wide text-lg text-white">{confirmation.date_label}</p>
                    <p className="gold-text font-display text-3xl font-bold mt-1">{confirmation.time}</p>
                  </div>
                  <p className="text-white/50 text-sm">
                    Recibirás un email de confirmación. Nos vemos pronto.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>

        <Reveal delay={0.2} className="mt-10">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            {FINAL_BENEFITS.map((b) => (
              <div key={b} className="flex items-center gap-2 text-xs md:text-sm text-white/70">
                <span className="text-[#D4AF37]">✓</span>
                <span className="font-display uppercase tracking-wide">{b}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
